// Support and benchmark data for the /photon pages. The raw records live in
// the release artifacts (data/photon/releases/*.json) and are aggregated by
// scripts/generate-photon-release-data.mjs into
// components/photon/generated/release-data.ts — this module re-exports that
// data and derives the summary facts the pages quote, so claims and data
// cannot drift apart.

import {
  MODEL_FAMILIES,
  RELEASE_MODELS,
  SUPPORT_MATRIX,
  LATEST_BENCHMARK,
} from "./generated/release-data";
import {
  throughputAdvantagePct,
} from "./performance-data";

export { MODEL_FAMILIES, RELEASE_MODELS, SUPPORT_MATRIX };
export type { ModelFamily, SupportRow } from "./release-types";

// ---- Current public benchmark result, derived from the latest complete
// benchmark release so the claims update with the data ----
export const THROUGHPUT_RESULT = (() => {
  const benchmark = LATEST_BENCHMARK;
  const env = benchmark.environment;
  const advantages = benchmark.throughput.map(throughputAdvantagePct);
  const concurrency = env.concurrency;
  const runtimes = [
    ...new Set(benchmark.throughput.map((cell) => cell.runtime)),
  ];
  const comparisons = runtimes.map((runtime) => {
    const cells = benchmark.throughput.filter(
      (cell) => cell.runtime === runtime,
    );
    return {
      runtime,
      label: env.runtimes[runtime],
      cells: cells.length,
      cellsAhead: cells.filter((cell) => cell.multiple > 1).length,
      minMultiple: Math.min(...cells.map((cell) => cell.multiple)),
      maxMultiple: Math.max(...cells.map((cell) => cell.multiple)),
    };
  });
  return {
    baseline: runtimes.map((runtime) => env.runtimes[runtime]).join(" and "),
    comparisons,
    comparisonSummary: comparisons
      .map((comparison) => `${comparison.cells} against ${comparison.label}`)
      .join(" and "),
    cellsMatched: benchmark.throughput.length,
    cellsAhead: benchmark.throughput.filter((cell) => cell.multiple > 1).length,
    advantageMinPct: +Math.min(...advantages).toFixed(1),
    advantageMaxPct: +Math.max(...advantages).toFixed(1),
    gpu: env.gpu,
    workload: env.workload,
    concurrency: `${Math.min(...concurrency)}–${Math.max(...concurrency)}`,
  };
})();

// Total cold-start time to ready, Photon vs the baseline engine, in seconds —
// derived from the phase-level rows (start-to-ready + first request).
export type ColdStart = {
  model: string;
  baseline: string;
  photonSeconds: number;
  baselineSeconds: number;
};

export const COLD_STARTS: ColdStart[] = LATEST_BENCHMARK.coldStart.map(
  (comparison) => ({
    model:
      LATEST_BENCHMARK.models.find((m) => m.id === comparison.modelId)?.model ??
      comparison.modelId,
    baseline: LATEST_BENCHMARK.environment.runtimes[comparison.runtime],
    photonSeconds: comparison.photonSeconds,
    baselineSeconds: comparison.baselineSeconds,
  }),
);

/** Percentage lower cold-start time than the baseline, per matched model. */
export const coldStartReductionPct = (c: ColdStart) =>
  (1 - c.photonSeconds / c.baselineSeconds) * 100;

/** Min/max cold-start reduction across the matched models, derived. */
export const COLD_START_RANGE = (() => {
  const pcts = COLD_STARTS.map(coldStartReductionPct);
  return { min: Math.min(...pcts), max: Math.max(...pcts) };
})();

/** "Aug 3, 2026" — stable across locales because locale and zone are pinned. */
export const formatMatrixDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

// ---- URL slugs and lookups for the generated detail pages ----
// (/photon/models/<family-slug>, /photon/hardware/<chip-slug>)

/** Lowercase URL slug: "Jetson AGX Orin" → "jetson-agx-orin". */
export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Distinct chips with production support, sorted for the hardware index. */
export const HARDWARE_CHIPS = [
  ...new Set(
    SUPPORT_MATRIX.filter((r) => r.status === "production").map((r) => r.chip),
  ),
].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

/** Resolve a URL slug back to a chip name ("h100" → "H100"). */
export const chipForSlug = (slug: string) =>
  HARDWARE_CHIPS.find((chip) => slugify(chip) === slug);

/** Production rows for one chip. */
export const rowsForChip = (chip: string) =>
  SUPPORT_MATRIX.filter((r) => r.chip === chip && r.status === "production");

/** Production rows for one model family name ("Qwen 3"). */
export const rowsForFamily = (family: string) =>
  SUPPORT_MATRIX.filter(
    (r) => r.family === family && r.status === "production",
  );

/** Family record by its URL slug ("qwen-3"). */
export const familyForSlug = (slug: string) =>
  MODEL_FAMILIES.find((f) => f.id === slug);

/** Family URL slug from its display name ("Qwen 3" → "qwen-3"). */
export const familySlugForName = (family: string) =>
  MODEL_FAMILIES.find((f) => f.name === family)?.id;

/** Chips a family runs on in production, deduplicated. */
export const chipsForFamily = (family: string) => [
  ...new Set(rowsForFamily(family).map((r) => r.chip)),
];
